import { Action } from "redux";

import {
    IFormProfile,
    ILocalizationItem,
    IMenuData,
    INotificationGroup,
    INotificationItem,
    IOpenedFormHistoryInfo,
    ITaskResultDetail,
    NotificationGroupId,
    NotificationItemId,
    OpenFormData,
} from "@multiweb/types/contracts";
import {
    IMainMenuItemActionParams,
    IMainMenuItemActionResult,
    IOpenedFormInfo,
    IOpenFormModalParams,
    ProfileChangeType,
} from "@multiweb/types/multiweb";
import { IResultAction, ReactActionId } from "@multiweb/types/react";
import { ApplicationTheme } from "@multiweb/uiShared/ApplicationTheme";
import {
    APPLY_THEME,
    CLOSE_FORM,
    EXECUTE_REACT_CONNECTOR_ACTION,
    FAVORITE_FORM_UPDATED,
    FAVORITE_FORMS_LOADED,
    FORM_CLOSED,
    FORM_CUSTOM_TITLE_CHANGED,
    FORM_ITEM_INSTANCE_CLOSE_RESTRICTIONS_UPDATED,
    FORM_OPENED,
    FORM_OVERLAY_CLICKED,
    FORM_PROFILE_CHANGED,
    FORM_SWITCHED,
    HIDE_ALL_FORMS,
    HIDE_FORM_OVERLAY,
    HIDE_LOADING_INDICATOR,
    HISTORY_FORMS_INFO_UPDATED,
    LOAD_FAVORITE_FORMS,
    LOAD_HISTORY_FORMS_INFO,
    LOAD_LOCAL_STORAGE_KEY,
    LOAD_LOCALIZATION_ITEMS,
    LOAD_MENU_ITEMS,
    LOAD_NOTIFICATION_GROUPS,
    LOAD_NOTIFICATION_ITEMS,
    LOAD_TASK_RESULT_DETAIL,
    LOAD_USER_INFO,
    LOCAL_STORAGE_KEY_LOADED,
    LOCALIZATION_ITEMS_LOADED,
    LOGOUT,
    MENU_ITEM_CUSTOM_ACTION_PROCESSED,
    MENU_ITEMS_LOADED,
    NOTIFICATION_GROUPS_LOADED,
    NOTIFICATION_ITEMS_LOADED,
    OPEN_FORM,
    OPEN_FORM_MODAL,
    PROCESS_MENU_ITEM_CUSTOM_ACTION,
    SELECTED_PROFILE_CHANGED,
    SET_FORM_AS_FAVORITE_REQUEST,
    SET_GROUP_NOTIFICATIONS_AS_READ,
    SET_LOCAL_STORAGE_KEY,
    SET_NOTIFICATION_ITEM_AS_READ,
    SET_REACT_CONNECTOR_MULTISELECT_MODE,
    SHOW_FORM_OVERLAY,
    SHOW_LOADING_INDICATOR,
    SHOW_SETTINGS,
    SWITCH_TO_FORM,
    TASK_RESULT_DETAIL_LOADED,
    USER_INFO_LOADED,
} from "./constants";

/**
 * Metoda pro otevření formuláře.
 */
export enum FormOpenMethod {
    /**
     * Otevře formulář vždy jako nový.
     */
    new,
    /**
     * Pokud již je formulář otevřený, použije ho. Jinak otevře nový.
     */
    newOrExisting,
    /**
     * Otevře formulář v nové záložce prohlížeče.
     */
    inTab,
}

/** Typ otevření modálního okna. */
export enum ModalWindowType {
    parametricForm,
    lov,
    errorMsg,
    modalForm,
}

/** Informace o názvu souboru */
export { FileNameParts } from "@multiweb/types/multiweb";

/** Informace o šifrování a podpisech */
export { ICryptographicInfo } from "@multiweb/types/multiweb";

/** Informace o podpisu */
export { ISignatureInfo } from "@multiweb/types/multiweb";

/**
 * Informace o uživateli.
 */
export type UserInfo = Readonly<{
    userId: number;
    userName: string;
    displayName: string;
}>;

export interface ILoadLocalStorageKeyAction extends Action<typeof LOAD_LOCAL_STORAGE_KEY> {
    readonly payload: {
        readonly key: string;
    };
}

export class ILoadLocalStorageKeyAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_LOCAL_STORAGE_KEY;
    }
}

export interface ISetLocalStorageKeyAction extends Action<typeof SET_LOCAL_STORAGE_KEY> {
    readonly payload: {
        readonly key: string;
        readonly value: string;
    };
}

export class ISetLocalStorageKeyAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_LOCAL_STORAGE_KEY;
    }
}

export interface IProcessMenuItemCustomActionAction extends Action<typeof PROCESS_MENU_ITEM_CUSTOM_ACTION> {
    readonly payload: {
        readonly menuItemParams: IMainMenuItemActionParams;
    };
}

export class IProcessMenuItemCustomActionAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === PROCESS_MENU_ITEM_CUSTOM_ACTION;
    }
}

export interface ILoadUserInfoAction extends Action<typeof LOAD_USER_INFO> {
}

export class ILoadUserInfoAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_USER_INFO;
    }
}

export interface ILoadMenuItemsAction extends Action<typeof LOAD_MENU_ITEMS> {
}

export class ILoadMenuItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_MENU_ITEMS;
    }
}

export interface ILoadLocalizationItemsAction extends Action<typeof LOAD_LOCALIZATION_ITEMS> {
    readonly payload: {
        readonly key: string;
        readonly locKeys: ReadonlyArray<string>;
    };
}

export class ILoadLocalizationItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_LOCALIZATION_ITEMS;
    }
}

export interface IShowLoadingIndicatorAction extends Action<typeof SHOW_LOADING_INDICATOR> {}

export class IShowLoadingIndicatorAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SHOW_LOADING_INDICATOR;
    }
}

export interface IHideLoadingIndicatorAction extends Action<typeof HIDE_LOADING_INDICATOR> {}

export class IHideLoadingIndicatorAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === HIDE_LOADING_INDICATOR;
    }
}

export interface IOpenFormAction extends Action<typeof OPEN_FORM> {
    readonly payload: {
        readonly formCode: string;
        readonly openMethod: FormOpenMethod;
        readonly openFormData?: OpenFormData;
    };
}

export class IOpenFormAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === OPEN_FORM;
    }
}

export interface IOpenFormModalAction extends Action<typeof OPEN_FORM_MODAL> {
    readonly payload: {
        readonly params: IOpenFormModalParams;
    };
}

export class IOpenFormModalAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === OPEN_FORM_MODAL;
    }
}

export interface ILogoutAction extends Action<typeof LOGOUT> {}

export class ILogoutAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOGOUT;
    }
}

export interface IShowSettingsAction extends Action<typeof SHOW_SETTINGS> {}

export class IShowSettingsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SHOW_SETTINGS;
    }
}

export interface IShowFormOverlayAction extends Action<typeof SHOW_FORM_OVERLAY> {}

export class IShowFormOverlayAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SHOW_FORM_OVERLAY;
    }
}

export interface IHideFormOverlayAction extends Action<typeof HIDE_FORM_OVERLAY> {}

export class IHideFormOverlayAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === HIDE_FORM_OVERLAY;
    }
}

export interface ILoadFavoriteFormsAction extends Action<typeof LOAD_FAVORITE_FORMS> {}

export class ILoadFavoriteFormsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_FAVORITE_FORMS;
    }
}

export interface ISetFormAsFavoriteRequestAction extends Action<typeof SET_FORM_AS_FAVORITE_REQUEST> {
    readonly payload: {
        readonly menuId: number;
        readonly formCode: string;
        readonly isFavorite: boolean;
        readonly profile?: IFormProfile;
    };
}

export class ISetFormAsFavoriteRequestAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_FORM_AS_FAVORITE_REQUEST;
    }
}

export interface ILoadNotificationItemsAction extends Action<typeof LOAD_NOTIFICATION_ITEMS> {
    readonly payload: {
        readonly notificationGroupId: string;
        readonly startIndex: number;
        readonly itemsCount: number;
    };
}

export class ILoadNotificationItemsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_NOTIFICATION_ITEMS;
    }
}

export interface ILoadNotificationGroupsAction extends Action<typeof LOAD_NOTIFICATION_GROUPS> {}

export class ILoadNotificationGroupsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_NOTIFICATION_GROUPS;
    }
}

export interface ISetGroupNotificationsAsReadAction extends Action<typeof SET_GROUP_NOTIFICATIONS_AS_READ> {
    readonly payload: {
        readonly notificationGroupId: NotificationGroupId;
        readonly panelItemId: string;
    };
}

export class ISetGroupNotificationsAsReadAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_GROUP_NOTIFICATIONS_AS_READ;
    }
}

export interface ISetNotificationItemAsReadAction extends Action<typeof SET_NOTIFICATION_ITEM_AS_READ> {
    readonly payload: {
        readonly itemId: NotificationItemId;
        readonly isRead: boolean;
    };
}

export class ISetNotificationItemAsReadAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_NOTIFICATION_ITEM_AS_READ;
    }
}

export interface ILoadTaskResultDetailAction extends Action<typeof LOAD_TASK_RESULT_DETAIL> {
    readonly payload: {
        readonly objId: number;
    };
}

export class ILoadTaskResultDetailAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_TASK_RESULT_DETAIL;
    }
}

export interface ILoadHistoryFormsInfoAction extends Action<typeof LOAD_HISTORY_FORMS_INFO> {}

export class ILoadHistoryFormsInfoAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOAD_HISTORY_FORMS_INFO;
    }
}

export interface ICloseFormAction extends Action<typeof CLOSE_FORM> {
    readonly payload: {
        readonly formInstanceId: string;
    };
}

export class ICloseFormAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === CLOSE_FORM;
    }
}

export interface IHideAllFormsAction extends Action<typeof HIDE_ALL_FORMS> {}

export class IHideAllFormsAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === HIDE_ALL_FORMS;
    }
}

export interface ISwitchToFormAction extends Action<typeof SWITCH_TO_FORM> {
    readonly payload: {
        readonly formInstanceId: string;
    };
}

export class ISwitchToFormAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SWITCH_TO_FORM;
    }
}

export interface IApplyThemeAction extends Action<typeof APPLY_THEME> {
    readonly payload: {
        readonly theme: ApplicationTheme;
    };
}

export class IApplyThemeAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === APPLY_THEME;
    }
}

export interface IExecuteReactConnectorActionAction extends Action<typeof EXECUTE_REACT_CONNECTOR_ACTION> {
    readonly payload: {
        readonly dataSourceKey: string;
        readonly actionId: ReactActionId;
        readonly dataObjectId: string;
    };
}

export class IExecuteReactConnectorActionAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === EXECUTE_REACT_CONNECTOR_ACTION;
    }
}

export interface ISetReactConnectorMultiselectModeAction extends Action<typeof SET_REACT_CONNECTOR_MULTISELECT_MODE> {
    readonly payload: {
        readonly dataSourceKey: string;
        readonly multiselectMode: boolean;
    };
}

export class ISetReactConnectorMultiselectModeAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SET_REACT_CONNECTOR_MULTISELECT_MODE;
    }
}

export interface IFavoriteFormsLoadedAction extends Action<typeof FAVORITE_FORMS_LOADED> {
    readonly payload: {
        readonly favoriteForms: ReadonlyArray<{ menuId: number; formCode: string; profileId?: number }>;
    };
}

export class IFavoriteFormsLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FAVORITE_FORMS_LOADED;
    }
}

export interface IHistoryFormsInfoUpdatedAction extends Action<typeof HISTORY_FORMS_INFO_UPDATED> {
    readonly payload: {
        readonly historyFormsInfo: ReadonlyArray<IOpenedFormHistoryInfo>;
    };
}

export class IHistoryFormsInfoUpdatedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === HISTORY_FORMS_INFO_UPDATED;
    }
}

export interface ILocalizationItemsLoadedAction extends Action<typeof LOCALIZATION_ITEMS_LOADED> {
    readonly payload: {
        readonly key: string;
        readonly localizationItems: ReadonlyMap<string, ILocalizationItem>;
    };
}

export class ILocalizationItemsLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOCALIZATION_ITEMS_LOADED;
    }
}

export interface ILocalStorageKeyLoadedAction extends Action<typeof LOCAL_STORAGE_KEY_LOADED> {
    readonly payload: {
        readonly key: string;
        readonly value: string;
    };
}

export class ILocalStorageKeyLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === LOCAL_STORAGE_KEY_LOADED;
    }
}

export type MenuItemCustomActionErrorPayload = {
    /**
     * Příznak, zda chyba nastala kvůli tomu, že není zaregistrována žádná implementace pro zpracování custom akcí, nebo zda chyba nastala až při samotném
     * zpracování.
     */
    handlerNotFound: boolean;
    errorMessage: string;
};
export interface IMenuItemCustomActionProcessedAction extends IResultAction<typeof MENU_ITEM_CUSTOM_ACTION_PROCESSED, IMainMenuItemActionResult, MenuItemCustomActionErrorPayload> {}

export interface IMenuItemsLoadedAction extends Action<typeof MENU_ITEMS_LOADED> {
    readonly payload: {
        readonly menuItems: Readonly<IMenuData>;
    };
}

export class IMenuItemsLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === MENU_ITEMS_LOADED;
    }
}

export interface INotificationGroupsLoadedAction extends Action<typeof NOTIFICATION_GROUPS_LOADED> {
    readonly payload: {
        readonly notificationGroups: readonly INotificationGroup[];
    };
}

export class INotificationGroupsLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === NOTIFICATION_GROUPS_LOADED;
    }
}

export interface INotificationItemsLoadedAction extends Action<typeof NOTIFICATION_ITEMS_LOADED> {
    readonly payload: {
        readonly notificationGroupId: NotificationGroupId;
        readonly notificationItems: ReadonlyArray<INotificationItem>;
    };
}

export class INotificationItemsLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === NOTIFICATION_ITEMS_LOADED;
    }
}

export interface ITaskResultDetailLoadedAction extends Action<typeof TASK_RESULT_DETAIL_LOADED> {
    readonly payload: {
        readonly taskResultDetail: ITaskResultDetail;
    };
}

export class ITaskResultDetailLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === TASK_RESULT_DETAIL_LOADED;
    }
}

export interface IUserInfoLoadedAction extends Action<typeof USER_INFO_LOADED> {
    readonly payload: {
        readonly userInfo: Readonly<UserInfo>;
    };
}

export class IUserInfoLoadedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === USER_INFO_LOADED;
    }
}

export interface IFavoriteFormUpdatedAction extends Action<typeof FAVORITE_FORM_UPDATED> {
    readonly payload: {
        readonly menuId: number;
        readonly formCode: string;
        readonly isFavorite: boolean;
        readonly profile: IFormProfile | undefined;
    };
}

export class IFavoriteFormUpdatedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FAVORITE_FORM_UPDATED;
    }
}

export interface IFormCustomTitleChangedAction extends Action<typeof FORM_CUSTOM_TITLE_CHANGED> {
    readonly payload: {
        readonly formInstanceId: string;
        readonly customTitle: string;
    };
}

export class IFormCustomTitleChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FORM_CUSTOM_TITLE_CHANGED;
    }
}

export interface IFormClosedAction extends Action<typeof FORM_CLOSED> {
    readonly payload: {
        readonly closedFormInfo: IOpenedFormInfo;
    };
}

export class IFormClosedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FORM_CLOSED;
    }
}

export interface IFormItemInstanceCloseRestrictionsUpdatedAction extends Action<typeof FORM_ITEM_INSTANCE_CLOSE_RESTRICTIONS_UPDATED> {
    readonly payload: {
        readonly formInstanceId: string;
        readonly hideCloseAction: boolean;
    };
}

export class IFormItemInstanceCloseRestrictionsUpdatedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FORM_ITEM_INSTANCE_CLOSE_RESTRICTIONS_UPDATED;
    }
}

export interface IFormOpenedAction extends Action<typeof FORM_OPENED> {
    readonly payload: {
        readonly openedFormInfo: IOpenedFormInfo;
        readonly hideWindowActions: boolean;
        readonly openInBackground: boolean;
    };
}

export class IFormOpenedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FORM_OPENED;
    }
}

export interface IFormOverlayClickedAction extends Action<typeof FORM_OVERLAY_CLICKED> {}

export class IFormOverlayClickedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FORM_OVERLAY_CLICKED;
    }
}

export class IFormProfileChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FORM_PROFILE_CHANGED;
    }
}

export interface IFormProfileChangedAction extends Action<typeof FORM_PROFILE_CHANGED> {
    readonly payload: {
        readonly type: ProfileChangeType;
        readonly profile: IFormProfile;
    };
}

export class ISelectedProfileChangedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === SELECTED_PROFILE_CHANGED;
    }
}

export interface ISelectedProfileChangedAction extends Action<typeof SELECTED_PROFILE_CHANGED> {
    readonly payload: {
        readonly formInstanceId: string;
        readonly profile?: IFormProfile;
    };
}

export interface IFormSwitchedAction extends Action<typeof FORM_SWITCHED> {
    readonly payload: {
        readonly switchedFormInfo: IOpenedFormInfo;
    };
}

export class IFormSwitchedAction {
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && instance.type === FORM_SWITCHED;
    }
}
